export type Channel<S, T> = {
  send: (data?: S) => void;
  receive: Promise<T>;
  resolve: (value: T | PromiseLike<T>) => void;
  reject: (reason: any) => void;
  close: () => void;
};

export type ChannelOptions = {
  timeout?: number;
};

export enum ChannelEnum {
  SHARE_HOVER_AUTHORIZATION = 'SHARE_HOVER_AUTHORIZATION',
}

export enum ErrorEnum {
  TIMEOUT = 'ERROR_TIMEOUT',
}

const DEFAULT_TIMEOUT = 30000;

let channelId = 0;
export const openChannel = <S, T>(
  name: ChannelEnum,
  options?: ChannelOptions,
): Channel<S, T> => {
  // Channels operate on the global HOVERApp object injected by the mobile app
  if (typeof window.HOVERApp === 'undefined') {
    throw new Error('Unable to open webview channel');
  }
  if (typeof window.HOVERApp.channels === 'undefined') {
    throw new Error('Channels are not supported by this webview');
  }

  // Ensure string key to prevent unintentional sparse array
  const id = `_${channelId}`;
  channelId += 1;

  let sent = false;
  let closed = false;
  let timeoutRef: NodeJS.Timeout;
  let timeoutMs = DEFAULT_TIMEOUT;
  if (typeof options?.timeout === 'number') {
    timeoutMs = parseInt(`${options.timeout}`, 10);
  }

  const close = () => {
    if (closed) throw new Error('Cannot close a closed channel');
    closed = true;
    clearTimeout(timeoutRef);
    if (typeof window.HOVERApp === 'undefined') return;
    if (typeof window.HOVERApp.channels === 'undefined') return;
    if (typeof window.HOVERApp.channels[name] === 'undefined') return;
    delete window.HOVERApp.channels[name][id];
  };

  let wrappedResolve: (_: T | PromiseLike<T>) => void = () => {};
  let wrappedReject: (_: any) => void = () => {};

  const receive = new Promise<T>((r, rr) => {
    wrappedResolve = r;
    wrappedReject = rr;
  });

  const resolve = (value: T | PromiseLike<T>) => {
    if (closed) throw new Error('Cannot resolve closed channel');
    close();
    return wrappedResolve(value);
  };

  const reject = (reason: any) => {
    if (closed) throw new Error('Cannot reject closed channel');
    close();
    return wrappedReject(new Error(reason));
  };

  const send = (data?: S) => {
    if (closed) throw new Error('Cannot send to closed channel');
    if (sent) throw new Error('Can only send on a channel once');
    sent = true;

    if (timeoutMs >= 0) {
      timeoutRef = setTimeout(() => {
        reject(ErrorEnum.TIMEOUT);
      }, timeoutMs);
    }

    window.ReactNativeWebView?.postMessage(
      JSON.stringify({ channel: name, id, data }),
    );
  };

  const channel = {
    send,
    receive,
    resolve,
    reject,
    close,
  };

  window.HOVERApp.channels[name] = window.HOVERApp.channels[name] || {};
  window.HOVERApp.channels[name][id] = channel;

  return channel;
};
